import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

import {
  PlaceOfSafetyTypeSchema,
  type AdHocPlaceOfSafetyUnbranded,
  type CuratedPlaceOfSafetyUnbranded,
  type PlaceOfSafetyType,
} from "@thalamos/common";
import { tk, type IncidentWorkItemWithContext } from "@thalamos/vision-common";

import api from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { PlaceOfSafetyPicker } from "../../components/FormBuilder/PlaceOfSafetyPicker/PlaceOfSafetyPicker.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { POS_TYPE_OPTION_KEYS, type PosRelinkTarget } from "./helpers.js";

type PickerValue =
  | CuratedPlaceOfSafetyUnbranded
  | AdHocPlaceOfSafetyUnbranded
  | ""
  | null
  | undefined;

interface RelinkFormValues {
  placeOfSafety: PickerValue;
  placeOfSafetyType: "" | PlaceOfSafetyType;
  reason: string;
  ccmNumber: string;
}

function isCurated(value: PickerValue): value is CuratedPlaceOfSafetyUnbranded {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string"
  );
}

export interface RelinkPlaceOfSafetyModalProps {
  incident: IncidentWorkItemWithContext;
  posTarget: PosRelinkTarget;
  onClose: () => void;
  onSaved: () => void;
}

export function RelinkPlaceOfSafetyModal({
  incident,
  posTarget,
  onClose,
  onSaved,
}: RelinkPlaceOfSafetyModalProps) {
  const { t } = useTranslation();
  const notifications = useToastNotifications();
  const incidentId = incident.incidentWorkItem.id;

  const initialValues: RelinkFormValues = {
    placeOfSafety: "",
    placeOfSafetyType: "",
    reason: "",
    ccmNumber: "",
  };

  // Label for the currently-recorded type, shown against the "leave unchanged"
  // option. Falls back to the raw stored value for legacy types not in the enum.
  const currentTypeLabel =
    posTarget.currentType == null
      ? null
      : posTarget.currentType in POS_TYPE_OPTION_KEYS
        ? t(POS_TYPE_OPTION_KEYS[posTarget.currentType as PlaceOfSafetyType])
        : posTarget.currentType;

  const handleSubmit = async (values: RelinkFormValues) => {
    const posChanged =
      isCurated(values.placeOfSafety) &&
      values.placeOfSafety.id !== posTarget.currentId;
    const typeChanged =
      values.placeOfSafetyType !== "" &&
      values.placeOfSafetyType !== posTarget.currentType;

    if (!posChanged && !typeChanged) {
      return;
    }

    const relink: {
      formId: string;
      placeOfSafetyId?: string;
      placeOfSafetyType?: { value: string; valueLabel: string };
    } = { formId: posTarget.formId };

    if (posChanged && isCurated(values.placeOfSafety)) {
      relink.placeOfSafetyId = values.placeOfSafety.id;
    }
    if (typeChanged && values.placeOfSafetyType !== "") {
      relink.placeOfSafetyType = {
        value: values.placeOfSafetyType,
        valueLabel: t(POS_TYPE_OPTION_KEYS[values.placeOfSafetyType]),
      };
    }

    try {
      const res = await api.incidentWorkItem.adminRelinkPlaceOfSafety(
        incidentId,
        {
          reason: values.reason.trim(),
          ccmNumber: values.ccmNumber.trim(),
          relinks: [relink],
        },
      );
      if (res.status === 204 || res.status === 200) {
        notifications.show(t(tk("adminRelinkPlaceOfSafety.saveSuccess")), {
          severity: "success",
        });
        onSaved();
        onClose();
      } else {
        const reasonMessage =
          typeof res.data === "object" &&
          res.data !== null &&
          "reason" in res.data
            ? String((res.data as { reason: string }).reason)
            : t(tk("adminRelinkPlaceOfSafety.saveError"));
        notifications.show(reasonMessage, { severity: "error" });
      }
    } catch {
      notifications.show(t(tk("adminRelinkPlaceOfSafety.saveError")), {
        severity: "error",
      });
    }
  };

  return (
    <PopupDialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      testId="relink-place-of-safety-dialog"
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {t(tk("adminRelinkPlaceOfSafety.title"))}
      </DialogTitle>
      <Formik<RelinkFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, isSubmitting }) => {
          const posChanged =
            isCurated(values.placeOfSafety) &&
            values.placeOfSafety.id !== posTarget.currentId;
          const typeChanged =
            values.placeOfSafetyType !== "" &&
            values.placeOfSafetyType !== posTarget.currentType;
          const canSubmit =
            (posChanged || typeChanged) &&
            values.reason.trim() !== "" &&
            values.ccmNumber.trim() !== "";

          return (
            <Form>
              <DialogContent>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      {t(tk("adminRelinkPlaceOfSafety.currentlyRecorded"))}
                    </Typography>
                    <Typography variant="h6">
                      {posTarget.currentName}
                    </Typography>
                  </Box>

                  <PlaceOfSafetyPicker
                    fieldName="placeOfSafety"
                    label={{
                      translationKey: tk(
                        "adminRelinkPlaceOfSafety.pickerLabel",
                      ),
                    }}
                    incidentWorkItem={incident}
                    disableManualEntry
                  />

                  <FormControl fullWidth>
                    <InputLabel id="relink-pos-type-label">
                      {t(tk("adminRelinkPlaceOfSafety.posType.label"))}
                    </InputLabel>
                    <Select
                      labelId="relink-pos-type-label"
                      name="placeOfSafetyType"
                      value={values.placeOfSafetyType}
                      label={t(tk("adminRelinkPlaceOfSafety.posType.label"))}
                      onChange={handleChange}
                      data-testid="pos-type-select"
                    >
                      <MenuItem value="">
                        {currentTypeLabel
                          ? t(
                              tk(
                                "adminRelinkPlaceOfSafety.posType.unchangedWithCurrent",
                              ),
                              { current: currentTypeLabel },
                            )
                          : t(tk("adminRelinkPlaceOfSafety.posType.unchanged"))}
                      </MenuItem>
                      {PlaceOfSafetyTypeSchema.options.map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(POS_TYPE_OPTION_KEYS[value])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label={t(tk("adminRelinkPlaceOfSafety.reasonLabel"))}
                    placeholder={t(
                      tk("adminRelinkPlaceOfSafety.reasonPlaceholder"),
                    )}
                    name="reason"
                    value={values.reason}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    required
                    data-testid="reason-input"
                  />
                  <TextField
                    label={t(tk("adminRelinkPlaceOfSafety.ccmNumberLabel"))}
                    placeholder={t(
                      tk("adminRelinkPlaceOfSafety.ccmNumberPlaceholder"),
                    )}
                    name="ccmNumber"
                    value={values.ccmNumber}
                    onChange={handleChange}
                    fullWidth
                    required
                    data-testid="ccm-number-input"
                  />

                  <Alert severity="info">
                    {t(tk("adminRelinkPlaceOfSafety.description"))}
                  </Alert>
                </Stack>
              </DialogContent>
              <DialogActions
                sx={{ px: 3, pb: 2, justifyContent: "space-between" }}
              >
                <Button onClick={onClose} color="error" variant="outlined">
                  {t(tk("adminRelinkPlaceOfSafety.cancel"))}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit || isSubmitting}
                  data-testid="confirm-relink-place-of-safety-btn"
                >
                  {t(tk("adminRelinkPlaceOfSafety.confirmButton"))}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </PopupDialog>
  );
}
